import { Link } from 'react-router-dom';
import { MANIFEST } from '../content/manifest';
import { useTranslation, type Locale } from '../i18n';

interface Props {
  lang: Locale;
  currentPath: string;
}

/**
 * Sidebar labels are localized via i18n keys:
 *   manifest.section.<sectionKey>
 *   manifest.leaf.<slug>
 * If a key isn't translated for the active locale, useTranslation falls back
 * to English and then to the literal key. We detect the literal-key case here
 * and fall back to the English string carried in manifest.ts.
 */
export default function Sidebar({ lang, currentPath }: Props) {
  const t = useTranslation();

  const localize = (key: string, fallback: string): string => {
    const v = t(key);
    return v === key ? fallback : v;
  };

  return (
    <nav>
      {MANIFEST.map((section) => (
        <div key={section.key} className="sb-section">
          <div className="sb-section-label">
            {localize(`manifest.section.${section.key}`, section.label)}
          </div>
          <ul className="sb-list">
            {section.items.map((item) => {
              const to = `/${lang}/${item.slug}`;
              const active = currentPath === to;
              return (
                <li key={item.slug}>
                  <Link to={to} className={active ? 'sb-link sb-link-active' : 'sb-link'}>
                    {localize(`manifest.leaf.${item.slug}`, item.label)}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
      <style>{sidebarCss}</style>
    </nav>
  );
}

const sidebarCss = `
.sb-section { margin-bottom: 20px; }
.sb-section-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--c-muted);
  margin: 0 0 8px 12px;
}
.sb-list { list-style: none; margin: 0; padding: 0; }
.sb-list li { margin: 0; }
.sb-link {
  display: block;
  padding: 6px 12px;
  border-radius: var(--radius-sm);
  font-size: 0.92rem;
  color: var(--c-fg);
  text-decoration: none;
  line-height: 1.4;
}
.sb-link:hover { background: var(--c-bg-alt); text-decoration: none; }
.sb-link-active {
  background: var(--c-bg-alt);
  font-weight: 600;
  color: var(--c-accent);
  border-left: 2px solid var(--c-accent);
  padding-left: 10px;
}
`;
